import { useCallback, useEffect, useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import {
  useTransportInfiniteQuery,
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"

import type {
  TransportOperationRequest,
  TransportOperationResponse,
} from "@/types_api/backend/transport/operations"
import type { RunRecord } from "@/types/api/RunRecord"

import type { UpdateRunTagsRequest } from "../utils/runTagsTransport"
import {
  normalizeRunRecord,
  normalizeRunRowSummary,
  type SummaryRunRow,
} from "../utils/transportRunNormalizers"

const RUN_ROWS_PAGE_SIZE = 100
const MAX_MULTI_RUN_IDS = 50

type RunRowsRequest = TransportOperationRequest<"getRunRows">
type RunOutliersResponse = TransportOperationResponse<"getRunOutliers">

export type RunOutlier = NonNullable<RunOutliersResponse["outliers"]>[number]
export type RunOutlierMetricSample = NonNullable<
  RunOutliersResponse["metric_samples"]
>[number]

export function useRunOutliersTransport(
  runId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = Boolean(runId) && (options?.enabled ?? true)

  return useTransportQuery<
    "getRunOutliers",
    {
      isComplete: boolean
      metricSamples: RunOutlierMetricSample[]
      outliers: RunOutlier[]
    }
  >({
    operation: "getRunOutliers",
    request: { run_id: runId ?? "" },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchInterval: (query) =>
        query.state.data?.is_complete === false ? 5_000 : false,
      refetchOnWindowFocus: false,
      select: (response) => ({
        isComplete: response.is_complete,
        metricSamples: response.metric_samples ?? [],
        outliers: response.outliers ?? [],
      }),
    },
  })
}

function sanitizeRunIds(runIds: string[]): string[] {
  const seen = new Set<string>()
  const sanitizedRunIds: string[] = []

  for (const rawRunId of runIds) {
    const runId = rawRunId.trim()
    if (!runId || seen.has(runId)) {
      continue
    }

    seen.add(runId)
    sanitizedRunIds.push(runId)
    if (sanitizedRunIds.length >= MAX_MULTI_RUN_IDS) {
      break
    }
  }

  return sanitizedRunIds
}

export function useRunMetadataTransport(
  runId: string | null | undefined,
  options?: {
    enabled?: boolean
    getRefetchInterval?: (run: RunRecord | null) => false | number
  },
) {
  const enabled = Boolean(runId) && (options?.enabled ?? true)

  return useTransportQuery<"getRunMetadata", { run: RunRecord | null }>({
    operation: "getRunMetadata",
    request: { run_id: runId ?? "" },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchInterval: options?.getRefetchInterval
        ? (query) =>
            options.getRefetchInterval?.(
              normalizeRunRecord(query.state.data?.run),
            )
        : undefined,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
      select: (response) => ({
        run: normalizeRunRecord(response.run),
      }),
    },
  })
}

/**
 * Imperative run-metadata fetcher for flows that only learn the run id at
 * action time (e.g. the copilot's re-run proposals). Same `getRunMetadata`
 * transport op as `useRunMetadataTransport`, mirroring the
 * `useSimulationOutputIdsFetcherTransport` fetcher pattern.
 */
export function useRunMetadataFetcherTransport() {
  const transportQueryUtils = useTransportQueryUtils()

  return useCallback(
    async (runId: string) => {
      const result = await transportQueryUtils.fetch("getRunMetadata", {
        run_id: runId,
      })
      return normalizeRunRecord(result.run)
    },
    [transportQueryUtils],
  )
}

export function useRunRowsTransport(
  runId: string | null | undefined,
  options?: {
    enabled?: boolean
    refetchInterval?: () => false | number
    refetchIntervalInBackground?: boolean
  },
) {
  const enabled = Boolean(runId) && (options?.enabled ?? true)

  const request = useMemo(
    (): RunRowsRequest => ({
      page_size: RUN_ROWS_PAGE_SIZE,
      run_id: runId ?? "",
    }),
    [runId],
  )

  const query = useTransportInfiniteQuery({
    operation: "getRunRows",
    request,
    getNextRequest: (lastPage, _allPages, previousRequest) => {
      if (!lastPage?.next_page_token || !previousRequest) {
        return null
      }

      return {
        ...previousRequest,
        page_token: lastPage.next_page_token,
      }
    },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchInterval: options?.refetchInterval,
      refetchIntervalInBackground: options?.refetchIntervalInBackground,
    },
  })

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query

  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [enabled, fetchNextPage, hasNextPage, isFetchingNextPage])

  const data = useMemo(
    () =>
      query.data?.pages.flatMap((page) =>
        page.rows.map(normalizeRunRowSummary),
      ),
    [query.data],
  )

  return {
    ...query,
    data,
    isLoading:
      query.isLoading || query.isFetchingNextPage || Boolean(query.hasNextPage),
  }
}

export function useMultiRunMetadataTransport(
  runIds: string[],
  options?: {
    enabled?: boolean
    staleTime?: number
  },
) {
  const sanitizedRunIds = useMemo(() => sanitizeRunIds(runIds), [runIds])
  const enabled = sanitizedRunIds.length > 0 && (options?.enabled ?? true)

  return useTransportQuery<"getMultiRunMetadata", { runs: RunRecord[] }>({
    operation: "getMultiRunMetadata",
    request: { run_ids: sanitizedRunIds },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnWindowFocus: false,
      staleTime: options?.staleTime ?? CACHE_PROFILES.lookup.staleTime,
      select: (response) => ({
        runs: response.runs
          .map(normalizeRunRecord)
          .filter((run): run is RunRecord => run !== null),
      }),
    },
  })
}

export function useMultiRunRowsTransport(
  runIds: string[],
  options?: {
    enabled?: boolean
    staleTime?: number
  },
) {
  const sanitizedRunIds = useMemo(() => sanitizeRunIds(runIds), [runIds])
  const enabled = sanitizedRunIds.length > 0 && (options?.enabled ?? true)

  return useTransportQuery<"getMultiRunRows", SummaryRunRow[]>({
    operation: "getMultiRunRows",
    request: { run_ids: sanitizedRunIds },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnWindowFocus: false,
      staleTime: options?.staleTime ?? CACHE_PROFILES.lookup.staleTime,
      select: (response) => response.rows.map(normalizeRunRowSummary),
    },
  })
}

export function useRunTransportInvalidation() {
  const transportQueryUtils = useTransportQueryUtils()

  const invalidateRunDetail = useCallback(
    (runId: string) =>
      Promise.all([
        transportQueryUtils.invalidate("getRunMetadata", { run_id: runId }),
        transportQueryUtils.invalidateOperation("getRunRows"),
        transportQueryUtils.invalidate("listSimulationOutputIds", {
          run_id: runId,
        }),
      ]),
    [transportQueryUtils],
  )

  const invalidateRunsLists = useCallback(
    () =>
      Promise.all([
        transportQueryUtils.invalidateOperation("getMultiRunMetadata"),
        transportQueryUtils.invalidateOperation("getRunsPageInsights"),
        transportQueryUtils.invalidateOperation(
          "listSimulatedConversationsKeyset",
        ),
        transportQueryUtils.invalidateOperation("listRunsKeyset"),
      ]),
    [transportQueryUtils],
  )

  return {
    invalidateRunDetail,
    invalidateRunsLists,
  }
}

export function useUpdateRunDisplayNameTransport() {
  return useTransportMutation("updateRunDisplayName")
}

export function useCancelRunTransport() {
  return useTransportMutation("cancelRun")
}

export function useDeleteRunTransport(options?: {
  onError?: (error: unknown) => void
  onSuccess?: () => void
}) {
  return useTransportMutation("deleteRun", options)
}

export function useSetRunPublicTransport(options?: {
  onSuccess?: (
    data: unknown,
    variables: { is_public: boolean; share_password?: string | null },
  ) => void
}) {
  return useTransportMutation("setRunPublic", options)
}

export function useUpdateRunTagsTransport(options?: {
  onMutate?: (variables: UpdateRunTagsRequest) => void
  onError?: (error: unknown) => void
  onSettled?: (
    data: unknown,
    error: unknown,
    variables: UpdateRunTagsRequest,
  ) => void
}) {
  return useTransportMutation("updateRunTags", options)
}
